import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import styles from './BurnInFileTable.module.css';
import GoToDashboardButton from './GoToDashboardButton';

const getApiBase = () =>
  process.env.REACT_APP_API_BASE ||
  (window.location.port === '3000' ? 'http://localhost:3001' : '');

const formatDateParam = (value) => {
  if (!value) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function BurnInFileTable() {
  const { t } = useTranslation();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState('');
  const [serialNumberOptions, setSerialNumberOptions] = useState([]);
  const [selectedSerialNumbers, setSelectedSerialNumbers] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedUploadIds, setSelectedUploadIds] = useState([]);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRows, setDetailRows] = useState([]);
  const [detailPage, setDetailPage] = useState(1);
  const [detailTotalPages, setDetailTotalPages] = useState(1);
  const [detailTotalRows, setDetailTotalRows] = useState(0);
  const [rowSearch, setRowSearch] = useState('');
  const [rowStepFilter, setRowStepFilter] = useState('');
  const [rowStatusFilter, setRowStatusFilter] = useState('');
  const detailLimit = 100;

  const loadUploads = useCallback(async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const response = await axios.get(`${apiBase}/api/burn-in/uploads`, {
        withCredentials: true,
        params: {
          search: search || undefined,
          serialNumbers: selectedSerialNumbers.length
            ? selectedSerialNumbers.map((option) => option.value).join(',')
            : undefined,
          startDate: formatDateParam(startDate) || undefined,
          endDate: formatDateParam(endDate) || undefined,
        },
      });
      setUploads(response.data || []);
      setSelectedUploadIds((current) => current.filter((id) => (response.data || []).some((item) => item.id === id)));
    } catch (error) {
      console.error('Failed to load burn-in file library:', error);
      setUploads([]);
    } finally {
      setLoading(false);
    }
  }, [endDate, search, selectedSerialNumbers, startDate]);

  const loadSerialNumberOptions = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const response = await axios.get(`${apiBase}/api/burn-in/filter-options`, {
        withCredentials: true,
      });
      const options = (response.data?.uutSerialNumber || [])
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .map((value) => ({ value, label: value }));
      setSerialNumberOptions(options);
    } catch (error) {
      console.error('Failed to load burn-in serial-number options:', error);
      setSerialNumberOptions([]);
    }
  }, []);

  const loadRows = useCallback(async (uploadId, page = 1) => {
    if (!uploadId) return;
    setDetailLoading(true);
    try {
      const apiBase = getApiBase();
      const [uploadResponse, rowsResponse] = await Promise.all([
        axios.get(`${apiBase}/api/burn-in/uploads/${uploadId}`, { withCredentials: true }),
        axios.get(`${apiBase}/api/burn-in/uploads/${uploadId}/rows`, {
          withCredentials: true,
          params: {
            page,
            limit: detailLimit,
            search: rowSearch || undefined,
            stepName: rowStepFilter || undefined,
            status: rowStatusFilter || undefined,
          },
        }),
      ]);

      setSelectedUpload(uploadResponse.data?.upload || null);
      setDetailRows(rowsResponse.data?.rows || []);
      setDetailPage(rowsResponse.data?.page || page);
      setDetailTotalPages(rowsResponse.data?.totalPages || 1);
      setDetailTotalRows(rowsResponse.data?.total || 0);
    } catch (error) {
      console.error('Failed to load burn-in upload rows:', error);
      setDetailRows([]);
      setDetailTotalPages(1);
      setDetailTotalRows(0);
    } finally {
      setDetailLoading(false);
    }
  }, [rowSearch, rowStatusFilter, rowStepFilter]);

  useEffect(() => {
    loadSerialNumberOptions();
  }, [loadSerialNumberOptions]);

  useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  useEffect(() => {
    if (selectedUpload?.id) {
      loadRows(selectedUpload.id, 1);
    }
  }, [loadRows, selectedUpload?.id]);

  useEffect(() => {
    if (!selectedUpload) return undefined;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeDetailModal();
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedUpload]);

  const closeDetailModal = () => {
    setSelectedUpload(null);
    setDetailRows([]);
    setDetailTotalRows(0);
    setDetailTotalPages(1);
    setDetailPage(1);
    setRowSearch('');
    setRowStepFilter('');
    setRowStatusFilter('');
  };

  const handleOpenUpload = (upload) => {
    setRowSearch('');
    setRowStepFilter('');
    setRowStatusFilter('');
    setSelectedUpload(upload);
  };

  const handleToggleUpload = (uploadId) => {
    setSelectedUploadIds((current) =>
      current.includes(uploadId) ? current.filter((id) => id !== uploadId) : [...current, uploadId]
    );
  };

  const handleToggleAllUploads = () => {
    if (selectedUploadIds.length === uploads.length) {
      setSelectedUploadIds([]);
      return;
    }
    setSelectedUploadIds(uploads.map((upload) => upload.id));
  };

  const refreshAfterDelete = async (deletedIds = []) => {
    if (selectedUpload?.id && deletedIds.includes(selectedUpload.id)) {
      setSelectedUpload(null);
      setDetailRows([]);
      setDetailTotalRows(0);
      setDetailTotalPages(1);
      setDetailPage(1);
    }
    setSelectedUploadIds((current) => current.filter((id) => !deletedIds.includes(id)));
    await loadUploads();
  };

  const handleDeleteOne = async (upload) => {
    const confirmed = window.confirm(`Vuoi eliminare il file burn-in "${upload.original_filename}"? Questa azione non può essere annullata.`);
    if (!confirmed) return;

    try {
      setDeleting(true);
      const apiBase = getApiBase();
      const response = await axios.delete(`${apiBase}/api/burn-in/uploads/${upload.id}`, { withCredentials: true });
      await refreshAfterDelete(response.data?.deletedUploadIds || [upload.id]);
    } catch (error) {
      window.alert(error.response?.data?.error || 'Impossibile eliminare il file burn-in.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedUploadIds.length) return;
    const confirmed = window.confirm(`Vuoi eliminare ${selectedUploadIds.length} file burn-in selezionat${selectedUploadIds.length === 1 ? 'o' : 'i'}? Questa azione non può essere annullata.`);
    if (!confirmed) return;

    try {
      setDeleting(true);
      const apiBase = getApiBase();
      const response = await axios.post(
        `${apiBase}/api/burn-in/uploads/delete-selected`,
        { uploadIds: selectedUploadIds },
        { withCredentials: true }
      );
      await refreshAfterDelete(response.data?.deletedUploadIds || selectedUploadIds);
    } catch (error) {
      window.alert(error.response?.data?.error || 'Impossibile eliminare i file burn-in selezionati.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAllVisible = async () => {
    if (!uploads.length) return;
    const confirmed = window.confirm(`Vuoi eliminare tutti i ${uploads.length} file burn-in attualmente visibili? Questa azione non può essere annullata.`);
    if (!confirmed) return;

    try {
      setDeleting(true);
      const apiBase = getApiBase();
      const response = await axios.post(
        `${apiBase}/api/burn-in/uploads/delete-all`,
        {
          search: search || undefined,
          serialNumbers: selectedSerialNumbers.length
            ? selectedSerialNumbers.map((option) => option.value).join(',')
            : undefined,
          startDate: formatDateParam(startDate) || undefined,
          endDate: formatDateParam(endDate) || undefined,
        },
        { withCredentials: true }
      );
      await refreshAfterDelete(response.data?.deletedUploadIds || uploads.map((upload) => upload.id));
    } catch (error) {
      window.alert(error.response?.data?.error || 'Impossibile eliminare i file burn-in visibili.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={styles.container}>
      <GoToDashboardButton />

      <div className={styles.content}>
        <header className={styles.pageHeader}>
          <div>
            <p className={styles.eyebrow}>{t('Burn-in data library')}</p>
            <h2 className={styles.title}>{t('View Burn-In Files')}</h2>
            <p className={styles.description}>
              {t('Browse every imported burn-in workbook from the database, then open one file to inspect all of its stored measurement rows.')}
            </p>
          </div>
        </header>

        <section className={styles.filters}>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('Filter by filename, sheet, or source label')}
            className={styles.filterInput}
          />
          <Select
            isMulti
            options={serialNumberOptions}
            value={selectedSerialNumbers}
            onChange={(values) => setSelectedSerialNumbers(values || [])}
            placeholder={t('Filter by Serial Number')}
            className={styles.multiSelect}
            classNamePrefix="burnInFileSelect"
          />
          <DatePicker
            selected={startDate}
            onChange={setStartDate}
            placeholderText={t('Imported from date')}
            className={styles.filterInput}
            dateFormat="dd/MM/yyyy"
            isClearable
          />
          <DatePicker
            selected={endDate}
            onChange={setEndDate}
            placeholderText={t('Imported to date')}
            className={styles.filterInput}
            dateFormat="dd/MM/yyyy"
            isClearable
          />
          <button type="button" onClick={loadUploads} className={styles.primaryButton}>
            {t('Apply Filters')}
          </button>
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setSelectedSerialNumbers([]);
              setStartDate(null);
              setEndDate(null);
            }}
            className={styles.secondaryButton}
          >
            {t('Reset Filters')}
          </button>
        </section>

        <section className={styles.uploadsPanel}>
            <div className={styles.panelHeader}>
              <div>
                <p className={styles.panelEyebrow}>{t('Stored workbooks')}</p>
                <h3>{t('Burn-in files in the database')}</h3>
              </div>
              <span className={styles.countBadge}>
                {t('{{count}} files', { count: uploads.length })}
              </span>
            </div>

            {uploads.length > 0 ? (
              <div className={styles.filters}>
                <button type="button" onClick={handleDeleteSelected} className={styles.secondaryButton} disabled={deleting || !selectedUploadIds.length}>
                  {selectedUploadIds.length ? `Elimina selezionati (${selectedUploadIds.length})` : 'Elimina selezionati'}
                </button>
                <button type="button" onClick={handleDeleteAllVisible} className={styles.secondaryButton} disabled={deleting || !uploads.length}>
                  Elimina tutti i file visibili
                </button>
              </div>
            ) : null}

            {loading ? (
              <div className={styles.emptyState}>{t('Loading burn-in files...')}</div>
            ) : uploads.length === 0 ? (
              <div className={styles.emptyState}>{t('No burn-in files found for the current filters.')}</div>
            ) : (
              <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={uploads.length > 0 && selectedUploadIds.length === uploads.length}
                          onChange={handleToggleAllUploads}
                          disabled={deleting}
                        />
                      </th>
                      <th>{t('ID')}</th>
                      <th>{t('Filename')}</th>
                      <th>{t('Imported')}</th>
                      <th>{t('Serial')}</th>
                      <th>{t('Station')}</th>
                      <th>{t('Rows')}</th>
                      <th>{t('Sources')}</th>
                      <th>{t('Fail')}</th>
                      <th>{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploads.map((upload) => (
                      <tr key={upload.id} className={selectedUpload?.id === upload.id ? styles.activeRow : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedUploadIds.includes(upload.id)}
                            onChange={() => handleToggleUpload(upload.id)}
                            disabled={deleting}
                          />
                        </td>
                        <td>{upload.id}</td>
                        <td>
                          <div className={styles.fileNameCell}>
                            <strong>{upload.original_filename}</strong>
                            <span>{upload.sheet_name || t('Sheet not detected')}</span>
                          </div>
                        </td>
                        <td>{upload.imported_at ? upload.imported_at.replace('T', ' ').slice(0, 19) : 'N/A'}</td>
                        <td>{upload.uut_serial_number || '—'}</td>
                        <td>{upload.station_id || '—'}</td>
                        <td>{upload.measurement_count ?? upload.parsed_rows ?? 0}</td>
                        <td>{upload.source_file_count ?? 0}</td>
                        <td>{upload.failed_count ?? 0}</td>
                        <td>
                          <button type="button" className={styles.primaryButton} onClick={() => handleOpenUpload(upload)} disabled={deleting}>
                            {t('View All')}
                          </button>
                          <button type="button" className={styles.secondaryButton} onClick={() => handleDeleteOne(upload)} disabled={deleting}>
                            Elimina
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </section>

        {selectedUpload ? (
          <div className={styles.detailModalBackdrop} onClick={closeDetailModal}>
            <section
              className={styles.detailModal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="burn-in-detail-title"
              onClick={(event) => event.stopPropagation()}
            >
              <header className={styles.detailModalHeader}>
                <div>
                  <p className={styles.panelEyebrow}>{t('Selected workbook')}</p>
                  <h3 id="burn-in-detail-title">{selectedUpload.original_filename}</h3>
                  <p className={styles.detailDescription}>
                    {t('Review every parsed burn-in row stored for this workbook, with filters for step, status, and free text.')}
                  </p>
                </div>
                <button type="button" className={styles.modalCloseButton} onClick={closeDetailModal}>
                  {t('Close')}
                </button>
              </header>

              <div className={styles.detailModalBody}>
                <div className={styles.summaryGrid}>
                  <div className={styles.summaryCard}><span>{t('Measurements')}</span><strong>{selectedUpload.measurement_count ?? selectedUpload.parsed_rows ?? 0}</strong></div>
                  <div className={styles.summaryCard}><span>{t('Unique steps')}</span><strong>{selectedUpload.unique_step_count ?? 0}</strong></div>
                  <div className={styles.summaryCard}><span>{t('Sources')}</span><strong>{selectedUpload.unique_source_count ?? selectedUpload.source_file_count ?? 0}</strong></div>
                  <div className={styles.summaryCard}><span>{t('Measured range')}</span><strong>{selectedUpload.first_measured_at ? `${selectedUpload.first_measured_at.slice(0, 10)} - ${(selectedUpload.last_measured_at || selectedUpload.first_measured_at).slice(0, 10)}` : t('Not available')}</strong></div>
                </div>

                <div className={styles.summaryGrid}>
                  <div className={styles.summaryCard}><span>{t('Station')}</span><strong>{selectedUpload.station_id || '—'}</strong></div>
                  <div className={styles.summaryCard}><span>{t('Operator')}</span><strong>{selectedUpload.operator_name || '—'}</strong></div>
                  <div className={styles.summaryCard}><span>{t('Serial')}</span><strong>{selectedUpload.uut_serial_number || '—'}</strong></div>
                  <div className={styles.summaryCard}><span>{t('Version SW')}</span><strong>{selectedUpload.version_sw || '—'}</strong></div>
                </div>

                <div className={styles.filters}>
                  <input
                    type="text"
                    value={rowSearch}
                    onChange={(event) => setRowSearch(event.target.value)}
                    placeholder={t('Search row text, source, value, or unit')}
                    className={styles.filterInput}
                  />
                  <input
                    type="text"
                    value={rowStepFilter}
                    onChange={(event) => setRowStepFilter(event.target.value)}
                    placeholder={t('Filter by step name')}
                    className={styles.filterInput}
                  />
                  <select
                    value={rowStatusFilter}
                    onChange={(event) => setRowStatusFilter(event.target.value)}
                    className={styles.filterInput}
                  >
                    <option value="">{t('All statuses')}</option>
                    <option value="PASS">PASS</option>
                    <option value="PASSED">PASSED</option>
                    <option value="FAIL">FAIL</option>
                    <option value="FAILED">FAILED</option>
                  </select>
                  <button type="button" onClick={() => loadRows(selectedUpload.id, 1)} className={styles.primaryButton}>
                    {t('Apply Row Filters')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setRowSearch('');
                      setRowStepFilter('');
                      setRowStatusFilter('');
                    }}
                    className={styles.secondaryButton}
                  >
                    {t('Clear Row Filters')}
                  </button>
                </div>

                <div className={styles.paginationBar}>
                  <span>{t('Showing {{shown}} of {{total}} stored rows', { shown: detailRows.length, total: detailTotalRows })}</span>
                  <div className={styles.paginationButtons}>
                    <button
                      type="button"
                      className={styles.secondaryButton}
                      disabled={detailLoading || detailPage <= 1}
                      onClick={() => loadRows(selectedUpload.id, detailPage - 1)}
                    >
                      {t('Previous')}
                    </button>
                    <span>{t('Page {{page}} / {{total}}', { page: detailPage, total: detailTotalPages })}</span>
                    <button
                      type="button"
                      className={styles.secondaryButton}
                      disabled={detailLoading || detailPage >= detailTotalPages}
                      onClick={() => loadRows(selectedUpload.id, detailPage + 1)}
                    >
                      {t('Next')}
                    </button>
                  </div>
                </div>

                {detailLoading ? (
                  <div className={styles.emptyState}>{t('Loading measurement rows...')}</div>
                ) : (
                  <div className={styles.tableScroll}>
                    <table className={`${styles.table} ${styles.detailTable}`}>
                      <thead>
                        <tr>
                          <th>{t('Row')}</th>
                          <th>{t('Source')}</th>
                          <th>{t('Sequence')}</th>
                          <th>{t('Step')}</th>
                          <th>{t('Status')}</th>
                          <th>{t('Measured At')}</th>
                          <th>{t('Value')}</th>
                          <th>{t('Units')}</th>
                          <th>{t('Limit Low')}</th>
                          <th>{t('Limit High')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailRows.length > 0 ? detailRows.map((row) => (
                          <tr key={row.id}>
                            <td>{row.row_index ?? 'N/A'}</td>
                            <td>{row.source_label || 'N/A'}</td>
                            <td>{row.sequence_text || 'N/A'}</td>
                            <td>{row.step_name || 'N/A'}</td>
                            <td>{row.status || 'N/A'}</td>
                            <td>{row.measured_at || 'N/A'}</td>
                            <td>{row.value_text ?? row.value_number ?? 'N/A'}</td>
                            <td>{row.units || 'N/A'}</td>
                            <td>{row.limit_low_raw || 'N/A'}</td>
                            <td>{row.limit_high_raw || 'N/A'}</td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="10" className={styles.emptyCell}>{t('No rows match the current filters.')}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
}
